import type { PlannerLeg, PlannerLegHeat as LegHeat } from './planner_models.js';

/** Qual campo da linha está pedindo a vez. `null` = a linha não pede nada. */
export type LegField = 'origin' | 'destination' | 'date';

/** O primeiro campo vazio de um trecho, na ordem em que se preenche. `null` =
 *  trecho completo. É a regra inteira de "quem pede a vez" num lugar só. */
export function firstPendingField(leg: PlannerLeg): LegField | null {
  if (!leg.origin.trim()) return 'origin';
  if (!leg.destination.trim()) return 'destination';
  if (!leg.date) return 'date';
  return null;
}

/** O primeiro trecho com campo vazio e QUAL campo (`index` 0-based) — o que a
 *  frase do multi nomeia. `null` = todos completos. */
export function firstPendingLeg(
  legs: readonly PlannerLeg[],
): { readonly index: number; readonly field: LegField } | null {
  for (const [index, leg] of legs.entries()) {
    const field = firstPendingField(leg);
    if (field) return { index, field };
  }
  return null;
}

/**
 * Encadeia as origens: a origem do trecho N passa a ser o destino do N-1 — **só
 * quando está VAZIA**.
 *
 * Sobrescrever apagaria uma escolha da pessoa, e open-jaw (partir de outra praça
 * depois de pousar numa) é caso real de viagem, não erro. O rótulo de display vai
 * junto com o IATA, senão a linha encadeada mostraria o código cru enquanto a de
 * cima mostra a cidade.
 */
export function chainOrigins(legs: PlannerLeg[]): PlannerLeg[] {
  return legs.map((leg, i) => {
    const prev = legs[i - 1];
    if (!prev?.destination.trim() || leg.origin.trim()) return leg;
    // Herdar não pode criar a rota impossível que a inversão acabou de desfazer:
    // depois de `swapEndpointsOnDuplicate` a linha fica `(vazio) → REC` e o
    // destino do trecho anterior TAMBÉM é REC — repor a origem devolveria o
    // `REC → REC`, e a pessoa ficaria presa num laço sem saída.
    if (prev.destination.trim() === leg.destination.trim()) return leg;
    return {
      ...leg,
      origin: prev.destination,
      ...(prev.destinationLabel ? { originLabel: prev.destinationLabel } : {}),
    };
  });
}

/** As duas pontas de uma rota como o kit as carrega: o IATA é o wire, o rótulo é
 *  display. `| undefined` explícito nos rótulos, e não opcional: quem aplica o
 *  resultado precisa APAGAR o rótulo velho da ponta que mudou de dono — um campo
 *  ausente deixaria "Recife" colado num GRU recém-chegado. */
export interface RouteEndpoints {
  origin: string;
  originLabel: string | undefined;
  destination: string;
  destinationLabel: string | undefined;
}

/**
 * **A inversão ao escolher a praça que já está na OUTRA ponta.** Quem abre o
 * campo DESTINO e escolhe a mesma praça que está na ORIGEM não está pedindo um
 * voo para a própria origem — voo assim não existe. A leitura provável é
 * "troquei as pontas", e é essa que esta função resolve, para as duas fileiras
 * que têm origem/destino: a binária e cada linha do multi.
 *
 * `null` = não é duplicado (fluxo normal): a outra ponta está vazia, ou a
 * escolha foi na MESMA ponta que já a tinha — reescolher a origem na origem é
 * confirmar o valor, não inverter.
 *
 * Devolve as DUAS pontas novas para quem chama aplicar num evento só. Dois
 * setters separados fariam a vez recalcular o próximo campo vazio duas vezes,
 * cada uma com metade da troca aplicada.
 *
 * O valor que estava na ponta EDITADA é o que atravessa para a outra — inclusive
 * quando ele é vazio, e é aí que nasce o laço que o #2197 pede: a ponta de onde
 * a praça saiu volta a pedir a vez.
 */
export function swapEndpointsOnDuplicate(
  endpoint: 'origin' | 'destination',
  chosen: { iata: string; label?: string },
  current: { origin: string; originLabel?: string; destination: string; destinationLabel?: string },
): RouteEndpoints | null {
  const other = endpoint === 'origin' ? current.destination : current.origin;
  if (!other.trim() || other.trim() !== chosen.iata.trim()) return null;
  const moved = endpoint === 'origin' ? current.origin : current.destination;
  const movedLabel = endpoint === 'origin' ? current.originLabel : current.destinationLabel;
  if (endpoint === 'origin') {
    return {
      origin: chosen.iata,
      originLabel: chosen.label,
      destination: moved,
      destinationLabel: movedLabel,
    };
  }
  return {
    origin: moved,
    originLabel: movedLabel,
    destination: chosen.iata,
    destinationLabel: chosen.label,
  };
}

/** O patch de uma linha do multi já passado pela regra acima: patch de ponta que
 *  duplica a outra vira patch de INVERSÃO; patch de data (ou de rota que não
 *  duplica) atravessa intacto — o mesmo objeto, para o chamador poder comparar
 *  por identidade. */
export function legPatchWithSwap(leg: PlannerLeg, patch: Partial<PlannerLeg>): Partial<PlannerLeg> {
  // Inspects `origin` first, then `destination` — callers emit one endpoint per patch.
  if (patch.origin !== undefined) {
    return (
      swapEndpointsOnDuplicate('origin', { iata: patch.origin, label: patch.originLabel }, leg) ??
      patch
    );
  }
  if (patch.destination !== undefined) {
    return (
      swapEndpointsOnDuplicate(
        'destination',
        { iata: patch.destination, label: patch.destinationLabel },
        leg,
      ) ?? patch
    );
  }
  return patch;
}

/**
 * Mantém a ordem das datas ao editar a data do trecho `index`: LIMPA a data de
 * todo trecho posterior que caia abaixo do novo piso. Sem isso, subir a data de um
 * trecho anterior deixa os posteriores fora de ordem e o echo `search_multi` sai
 * com datas decrescentes — é invariante de correção, não de interface.
 */
export function clearDatesBelow(legs: PlannerLeg[], index: number): PlannerLeg[] {
  const floor = legs[index]?.date;
  if (!floor) return legs;
  return legs.map((leg, i) =>
    i > index && leg.date && leg.date < floor ? { ...leg, date: undefined } : leg,
  );
}

/** Piso day-granular da data do trecho: a data do trecho anterior (ou hoje no 1º /
 *  enquanto o anterior não tem data) — garante `date[N] >= date[N-1]`. */
export function dateFloor(legs: PlannerLeg[], index: number, todayIso: string): string {
  const prev = legs[index - 1];
  return prev?.date || todayIso;
}

/**
 * **Traslado: a viagem não continua de onde parou.** A origem é encadeada por
 * padrão, então quando ela DIVERGE do destino anterior é porque a pessoa trocou na
 * mão — pousa numa praça e parte de outra. Não é erro, é fato da viagem, e por isso
 * vira selo neutro colado na origem (onde a divergência acontece) em vez de aviso.
 */
export function isTransfer(legs: PlannerLeg[], index: number): boolean {
  const prev = legs[index - 1];
  const current = legs[index];
  if (!prev?.destination.trim() || !current?.origin.trim()) return false;
  return prev.destination !== current.origin;
}

/** Pronto pra cotar: todo trecho tem origem, destino e data. */
export function legsComplete(legs: PlannerLeg[]): boolean {
  return legs.every((leg) => firstPendingField(leg) == null);
}

/** Completo E coerente: nenhum trecho com origem == destino (rota impossível é
 *  erro de input — sem este gate, GRU→GRU ia ao vendor e voltava como falha
 *  genérica, CC-5). */
export function legsValid(legs: PlannerLeg[]): boolean {
  return legsComplete(legs) && legs.every((leg) => leg.origin.trim() !== leg.destination.trim());
}

/**
 * A viagem multi numa cadeia de praças: a origem de CADA trecho mais o destino do
 * último (`GRU → REC → SSA → GRU`). É o resumo da linha colapsada, e ele não
 * repete praça justamente porque a origem de um trecho é o destino do anterior —
 * listar origem+destino por trecho diria "REC → REC" no meio de toda viagem
 * encadeada.
 *
 * Pontas ainda vazias saem como `''`: quem MOSTRA decide o placeholder (o rótulo
 * é copy localizada, e esta função é pura).
 */
export function legsChain(legs: PlannerLeg[]): string[] {
  const last = legs.at(-1);
  if (!last) return [];
  return [...legs.map((leg) => leg.origin.trim()), last.destination.trim()];
}

/** Onde a vez vai depois de mexer no trecho `index`: primeiro dentro dele, senão no
 *  próximo trecho ADIANTE que ainda tem campo vazio, senão em nenhum lugar — e
 *  "nenhum lugar" é o estado de viagem completa, em que nada fica aberto. */
export function nextFocus(
  legs: PlannerLeg[],
  legIds: string[],
  index: number,
): { id: string | null; field: LegField | null } {
  const here = legs[index] ? firstPendingField(legs[index]) : null;
  if (here) return { id: legIds[index] ?? null, field: here };
  const ahead = legs.findIndex((leg, i) => i > index && firstPendingField(leg) != null);
  const aheadLeg = ahead >= 0 ? legs[ahead] : undefined;
  if (aheadLeg) return { id: legIds[ahead] ?? null, field: firstPendingField(aheadLeg) };
  return { id: null, field: null };
}

/** Qual trecho abre quando o card monta: o primeiro incompleto. Viagem completa
 *  abre com tudo colapsado — se não há campo pedindo nada, não há formulário. */
export function seedEditingLeg(legs: PlannerLeg[], legIds: string[]): string | null {
  const pending = legs.findIndex((leg) => firstPendingField(leg) != null);
  return pending >= 0 ? (legIds[pending] ?? null) : null;
}

/**
 * Qual campo da perna aberta pede a vez quando o card monta. Só uma PRAÇA: a
 * data nunca é semente — o anel na data entra quando as praças são respondidas
 * (`nextFocus`), e o popover dela só abre por gesto no QUANDO, por pedido
 * explícito do host (`initialDatesOpen`) ou pelo preenchimento guiado da
 * fileira larga (`useGuidedFill`, que aí também põe o anel). Sem guia, uma rota
 * completa sem data (o agente preencheu as praças) nasce sem anel.
 */
export function seedLegFocus(legs: PlannerLeg[], legIds: string[]): LegField | null {
  const index = legIds.indexOf(seedEditingLeg(legs, legIds) ?? '');
  const seeded = index >= 0 ? legs[index] : undefined;
  const pending = seeded ? firstPendingField(seeded) : null;
  return pending === 'date' ? null : pending;
}

/**
 * Pra ONDE a edição vai quando a perna aberta é removida: o próximo trecho
 * pendente (mesma régua do mount), senão o ÚLTIMO — nunca "nenhum lugar", pois
 * remover a aberta não pode deixar a viagem sem edição em cena. Pura — só o
 * `removeLeg` do form chama, depois de já ter decidido que a perna removida
 * ERA a aberta (o card não reatribui quando remove uma perna diferente).
 */
export function reassignEditingLeg(
  legs: PlannerLeg[],
  legIds: string[],
): { editingLegId: string | null; focus: LegField | null } {
  const target = seedEditingLeg(legs, legIds) ?? legIds.at(-1) ?? null;
  const targetIndex = legIds.indexOf(target ?? '');
  return {
    editingLegId: target,
    focus: targetIndex >= 0 && legs[targetIndex] ? firstPendingField(legs[targetIndex]) : null,
  };
}

/** A rota de onde um trecho é semeado. Os rótulos são só display — o wire é IATA.
 *  Não exportada: os chamadores passam objeto literal, e um export sem consumidor
 *  derruba o ratchet do knip. */
interface SeedRoute {
  origin: string;
  destination: string;
  originLabel?: string;
  destinationLabel?: string;
}

/**
 * Os DOIS trechos com que o multi abre. Multi-trecho é ≥2 por definição, e abrir
 * com um cobraria um clique de 100% das pessoas.
 *
 * O 1º é sempre a rota dada. O 2º depende de onde a pessoa veio: com `returnDate`
 * (ela estava em IDA E VOLTA) ele nasce sendo a VOLTA — rota invertida, data
 * digitada, rótulos trocados junto; sem ele, nasce vazio com a origem encadeada.
 *
 * Consequência que os chamadores precisam saber: vindo de ida e volta a viagem sai
 * COMPLETA daqui, então `seedEditingLeg` não acha pendência e nada abre. Quem
 * reabre um trecho a partir daí é o clique no resumo.
 */
export function seedLegs(route: SeedRoute, departDate?: string, returnDate?: string): PlannerLeg[] {
  return chainOrigins([
    {
      origin: route.origin,
      destination: route.destination,
      ...(route.originLabel ? { originLabel: route.originLabel } : {}),
      ...(route.destinationLabel ? { destinationLabel: route.destinationLabel } : {}),
      ...(departDate ? { date: departDate } : {}),
    },
    // Vindo de IDA E VOLTA, o trecho 2 JÁ é conhecido: é a volta que a pessoa
    // digitou. Semear vazio a fazia perder a data e redigitar a rota invertida —
    // e quem vira pro roteiro num card de ida-e-volta quase sempre quer INSERIR
    // uma parada, não recomeçar. Os rótulos acompanham as pontas que descrevem,
    // invertidos junto (o mesmo cuidado do `seedRouteFromLive`).
    returnDate
      ? {
          origin: route.destination,
          destination: route.origin,
          ...(route.destinationLabel ? { originLabel: route.destinationLabel } : {}),
          ...(route.originLabel ? { destinationLabel: route.originLabel } : {}),
          date: returnDate,
        }
      : { origin: '', destination: '' },
  ]);
}

/**
 * A rota VIVA do corpo binário, pronta para semear o trecho 1.
 *
 * Existe porque os trechos são semeados uma vez, no mount: quem troca o destino
 * no corpo binário e depois entra no multi via `seedLegs(ui.route)` veria a rota
 * de quando o card montou — e o heat seria pedido para ela.
 *
 * O rótulo só acompanha o IATA que ele descreve: carregar o `destinationLabel`
 * do bloco num IATA que a pessoa acabou de trocar imprimiria "Nova York · JFK"
 * ao lado de GIG. Sem rótulo a linha mostra o IATA cru, que é honesto.
 */
export function seedRouteFromLive(live: SeedRoute, block: SeedRoute): SeedRoute {
  // O rótulo vivo (o pick da pessoa) manda; na falta dele, o do bloco serve só se
  // ainda descrever o MESMO IATA.
  const originLabel =
    live.originLabel ?? (live.origin === block.origin ? block.originLabel : undefined);
  const destinationLabel =
    live.destinationLabel ??
    (live.destination === block.destination ? block.destinationLabel : undefined);
  return {
    origin: live.origin,
    destination: live.destination,
    ...(originLabel ? { originLabel } : {}),
    ...(destinationLabel ? { destinationLabel } : {}),
  };
}

/**
 * O pacote completo de um (re)seed: trechos, ids estáveis, qual abre e que campo
 * pede a vez. Os quatro juntos porque têm de ser consistentes — um `editingLegId`
 * apontando para um id que não existe tira o dono do calendário.
 */
export function seedLegsState(
  route: SeedRoute,
  departDate: string | undefined,
  idPrefix: string,
  returnDate?: string,
): { legs: PlannerLeg[]; ids: string[]; openId: string | null; focus: LegField | null } {
  const legs = seedLegs(route, departDate, returnDate);
  const ids = legs.map((_, i) => `${idPrefix}${String(i)}`);
  const openId = seedEditingLeg(legs, ids);
  const at = ids.indexOf(openId ?? '');
  const open = at >= 0 ? legs[at] : undefined;
  return { legs, ids, openId, focus: open ? firstPendingField(open) : null };
}

/**
 * A chave do heat de uma perna: rota + mês em que a janela pedida COMEÇA. `null`
 * quando a rota está incompleta — aí não há o que pedir.
 *
 * Existe para o card pedir o heat UMA vez por combinação, e não a cada render: uma
 * viagem de 4 trechos já custa até 4 chamadas ao price-calendar, e sem chave o
 * efeito dispararia em loop. Pura de propósito — a regra que segura custo de
 * provider tem de ser testável sem montar card.
 */
export function legHeatKey(leg: PlannerLeg, fromMonth: string): string | null {
  const origin = leg.origin.trim();
  const destination = leg.destination.trim();
  if (!origin || !destination) return null;
  // Mesma chave em que a resposta vai MORAR (`routeHeatKey`) — de propósito: o card
  // pede a chave K e o heat aterrissa em K, então "já pedi" e "já tenho" falam a
  // mesma língua. Formatar aqui de novo abriria as duas para divergir.
  return routeHeatKey(origin, destination, fromMonth);
}

/**
 * Os meses visíveis já estão cobertos pelo heat da perna?
 *
 * Cobertura = o mês tem ao menos UM dia no heat. O servidor devolve só os dias com
 * preço, então um mês sem dia nenhum é um mês sem heat útil — pedido ou não (além do
 * horizonte de publicação das cias, o pedido volta vazio). Sem heat, nada está coberto.
 * Consequência aceita: ida-e-volta por uma janela que o provider não tem re-pede a cada
 * passada; quem corta o loop é o dedupe do último pedido, não esta função.
 *
 * É esta a pergunta que decide pedir heat, e não "esta perna tem algum heat?": cada
 * janela mora na própria entrada (ver `routeHeatKey`), então a perna pode ter heat de
 * sobra e nenhum dele descrever a janela que a grade mostra — e aí o pager andava
 * sobre meses permanentemente neutros.
 */
export function legHeatCovers(heat: LegHeat | undefined, months: string[]): boolean {
  if (!heat?.days.length) return false;
  const covered = new Set(heat.days.map((d) => d.date.slice(0, 7)));
  return months.every((m) => covered.has(m));
}

/**
 * A chave do `routeHeat` do bloco: a ROTA **mais o mês em que a janela começa** —
 * porque heat não é de uma rota, é de uma rota NUMA janela.
 *
 * A janela na chave é estrutural, não zelo. Sem ela, duas coisas quebravam sem guarda
 * possível: uma resposta ATRASADA da janela A sobrescrevia a janela B recém-pintada
 * (mesma entrada), e duas pernas de mesma rota com janelas visíveis diferentes
 * disputavam a única entrada, re-pedindo a cada troca de trecho. Com a janela na chave,
 * cada resposta escreve na PRÓPRIA entrada: chegar tarde deixou de ser um perigo, e
 * voltar a uma janela já buscada passou a ser de graça (a entrada ainda está lá).
 *
 * Skew de histórico: bloco persistido antes desta mudança tem chave só-rota
 * (`'GRU:JFK'`), que ninguém mais lê — a conversa antiga reabre com a grade neutra e se
 * cura no primeiro read (a cobertura não fecha, o card pede). Não há migração de chave.
 */
export function routeHeatKey(origin: string, destination: string, fromMonth: string): string {
  return `${origin.trim()}:${destination.trim()}:${fromMonth}`;
}

/**
 * O heat de uma perna numa JANELA, lido do BLOCO e casado por rota + janela.
 *
 * Duas fontes, na ordem:
 *
 * ① `routeHeat[rota:janela]` — o que o shell escreveu a partir do READ por perna.
 * ② `outbound`, QUANDO a perna é a rota do próprio bloco **e a janela pedida é a do
 *    bloco**. É o caso dominante: quem entra no multi a partir de um card binário tem a
 *    perna 1 igual à rota dele, e o calor já está ali — pedir de novo seria gastar
 *    provider para redescobrir o que a tela já sabe. A checagem de janela é o que
 *    impede a queda de mentir: `outbound` descreve a janela do bloco, e devolvê-lo pra
 *    uma janela paginada pintaria (ou "cobriria") meses que ele não descreve.
 *
 * Nunca por índice de perna: o card semeia `legs` no mount e é dono delas, e o bloco
 * pode não ter `legs` nenhuma. Heat por índice aterrissaria na perna errada — a
 * mentira "o trecho 3 custa o que o trecho 1 custa" entrando pela porta de trás.
 */
export function legHeatFor(
  block:
    | {
        route: { origin: string; destination: string };
        window: { fromMonth: string };
        outbound?: LegHeat;
        routeHeat?: Record<string, LegHeat>;
      }
    | undefined,
  leg: PlannerLeg | undefined,
  fromMonth: string,
): LegHeat | undefined {
  if (!block || !leg) return undefined;
  const origin = leg.origin.trim();
  const destination = leg.destination.trim();
  if (!origin || !destination) return undefined;
  const fromRead = block.routeHeat?.[routeHeatKey(origin, destination, fromMonth)];
  if (fromRead) return fromRead;
  const isBlockRoute =
    block.route.origin.trim() === origin && block.route.destination.trim() === destination;
  return isBlockRoute && fromMonth === block.window.fromMonth ? block.outbound : undefined;
}
